import { useState, useRef } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { mockTracks, MusicTrack } from '../../mocks/music';

function AudioPlayer({ track }: { track: MusicTrack }) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play().catch(() => {}); setPlaying(true); }
  };

  const onTimeUpdate = () => {
    if (!audioRef.current) return;
    const pct = audioRef.current.duration ? (audioRef.current.currentTime / audioRef.current.duration) * 100 : 0;
    setProgress(pct);
  };

  const onEnded = () => { setPlaying(false); setProgress(0); };

  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !audioRef.current.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = pct * audioRef.current.duration;
    setProgress(pct * 100);
  };

  return (
    <div className="flex items-center gap-4 mt-3">
      <audio ref={audioRef} src={track.audioUrl} onTimeUpdate={onTimeUpdate} onEnded={onEnded} />
      <button onClick={toggle} className="w-11 h-11 flex items-center justify-center rounded-full bg-brand-yellow hover:bg-brand-orange transition-colors duration-200 cursor-pointer flex-shrink-0">
        <i className={`${playing ? 'ri-pause-fill' : 'ri-play-fill'} text-black text-lg`} />
      </button>
      <div className="flex-1 cursor-pointer" onClick={seek}>
        <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-brand-yellow rounded-full transition-all duration-100" style={{ width: `${progress}%` }} />
        </div>
      </div>
      <span className="font-heading text-white/40 text-xs whitespace-nowrap">{track.duration}</span>
      {track.spotifyUrl && (
        <a
          href={track.spotifyUrl}
          target="_blank"
          rel="noopener noreferrer"
          title="Stream on Spotify"
          className="flex-shrink-0 flex items-center gap-1.5 whitespace-nowrap font-heading font-semibold text-xs tracking-widest uppercase px-3 py-1.5 rounded-full bg-[#1DB954]/10 text-[#1DB954] border border-[#1DB954]/30 hover:bg-[#1DB954]/20 transition-colors duration-200 cursor-pointer"
        >
          <i className="ri-spotify-fill text-sm" />
          Stream
        </a>
      )}
    </div>
  );
}

export default function Music() {
  const [filter, setFilter] = useState('All');
  const albums = ['All', ...Array.from(new Set(mockTracks.map((t) => t.album).filter(Boolean)))];
  const filtered = filter === 'All' ? mockTracks : mockTracks.filter((t) => t.album === filter);

  return (
    <div className="bg-brand-dark min-h-screen">
      <Navbar />
      <main className="pt-20">
        {/* Header */}
        <section className="relative py-20 px-6 md:px-12 overflow-hidden">
          <div className="absolute inset-0">
            <img src="https://public.readdy.ai/ai/img_res/cfb8f7ee863ed6f0dae64b75659d5bfb.jpg" alt="" className="w-full h-full object-cover object-center opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-b from-brand-dark/80 to-brand-dark" />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto">
            <p className="font-heading text-brand-orange tracking-[0.3em] uppercase text-xs mb-2">Stream &amp; Buy</p>
            <h1 className="font-display text-6xl md:text-7xl text-white tracking-wide mb-4">THE <span className="text-brand-yellow">MUSIC</span></h1>
            <p className="font-body text-white/50 max-w-xl">Listen to snippets of Dirty Birdy&apos;s tracks. Click play to preview, then grab the full track.</p>
          </div>
        </section>

        {/* Filter Tabs */}
        <section className="px-6 md:px-12 pb-6 bg-brand-dark">
          <div className="max-w-7xl mx-auto flex flex-wrap gap-2">
            {albums.map((a) => (
              <button key={a} onClick={() => setFilter(a)} className={`whitespace-nowrap font-heading text-xs tracking-widest uppercase px-4 py-2 transition-colors duration-200 cursor-pointer ${filter === a ? 'bg-brand-yellow text-black' : 'border border-white/20 text-white/60 hover:border-brand-yellow hover:text-brand-yellow'}`}>{a}</button>
            ))}
          </div>
        </section>

        {/* Track List */}
        <section className="py-8 px-6 md:px-12 bg-brand-dark pb-20">
          <div className="max-w-7xl mx-auto flex flex-col gap-5">
            {filtered.map((track, i) => (
              <div key={track.id} className="bg-brand-card p-5 flex flex-col md:flex-row gap-5 items-start">
                <div className="relative w-20 h-20 flex-shrink-0 overflow-hidden">
                  <img src={track.coverUrl} alt={track.title} className="w-full h-full object-cover object-top" />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                    <span className="font-display text-white text-lg">{String(i + 1).padStart(2, '0')}</span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h3 className="font-display text-2xl text-white tracking-wide">{track.title}</h3>
                    {track.album && (
                      <span className="font-heading text-brand-orange text-xs tracking-widest uppercase">{track.album} &bull; {track.year}</span>
                    )}
                  </div>
                  {track.description && (
                    <p className="font-body text-white/40 text-sm">{track.description}</p>
                  )}
                  {track.appleMusicEmbedUrl ? (
                    <div className="mt-3 w-full" style={{ maxWidth: '660px', height: '75px', overflow: 'hidden', position: 'relative' }}>
                      <div style={{ transform: 'scale(0.5)', transformOrigin: 'top left', width: '200%', height: '150px' }}>
                        <iframe
                          src={track.appleMusicEmbedUrl}
                          height="150"
                          frameBorder="0"
                          allow="autoplay *; encrypted-media *;"
                          sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation"
                          style={{ width: '100%', overflow: 'hidden', background: 'transparent', borderRadius: '24px', display: 'block' }}
                        />
                      </div>
                    </div>
                  ) : track.spotifyEmbedUrl ? (
                    <div className="mt-3 w-full">
                      <iframe
                        src={track.spotifyEmbedUrl}
                        width="100%"
                        height="152"
                        frameBorder="0"
                        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                        loading="lazy"
                        style={{ borderRadius: '12px' }}
                      />
                    </div>
                  ) : (
                    <AudioPlayer track={track} />
                  )}
                  <div className="mt-3">
                    <a href={track.buyUrl} target="_blank" rel="noopener noreferrer" className="whitespace-nowrap font-heading font-semibold text-xs tracking-widest uppercase bg-brand-yellow text-black px-5 py-2.5 hover:bg-brand-orange hover:text-white transition-colors duration-200 cursor-pointer text-center inline-block">Buy Track</a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
